/**
 * Tracks the visual viewport so note modals can stay above the soft
 * keyboard (same job KeyboardAvoidingView does on native).
 *
 * `keyboardOpen` is true when the visual viewport is meaningfully shorter
 * than the layout viewport — used to drop safe-area bottom padding, which
 * otherwise leaves a gap between the footer and the keyboard.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Shrink threshold (px) before we treat the soft keyboard as open. */
const KEYBOARD_OPEN_DELTA = 120;
/** Viewport metrics settle a beat after the page is on screen again. */
const SETTLE_DELAYS_MS = [0, 150, 400];

export interface VisualViewportBox {
  height: number;
  offsetTop: number;
  keyboardOpen: boolean;
  /** Re-measure after a trip out of the page (camera, file picker). */
  remeasure: () => void;
}

export function useVisualViewport(): VisualViewportBox {
  const [box, setBox] = useState({
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    offsetTop: 0,
    keyboardOpen: false,
  });
  const settleRef = useRef<() => void>(() => {});

  useEffect(() => {
    const vv = window.visualViewport;
    const timers: number[] = [];

    const update = () => {
      const height = vv?.height ?? window.innerHeight;
      const offsetTop = vv?.offsetTop ?? 0;
      const layoutHeight = window.innerHeight;
      const keyboardOpen = layoutHeight - height - offsetTop > KEYBOARD_OPEN_DELTA;
      setBox((prev) => (
        prev.height === height && prev.offsetTop === offsetTop && prev.keyboardOpen === keyboardOpen
          ? prev
          : { height, offsetTop, keyboardOpen }
      ));
    };

    /*
     * Coming back from the camera does not reliably fire a viewport event, so
     * the modal would keep the size it had while the camera was up until the
     * next touch. Re-measure across the following frames instead.
     */
    const settle = () => {
      update();
      for (const delay of SETTLE_DELAYS_MS) timers.push(window.setTimeout(update, delay));
    };
    settleRef.current = settle;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') settle();
    };

    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('pageshow', settle);
    window.addEventListener('focus', settle);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      for (const id of timers) window.clearTimeout(id);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('pageshow', settle);
      window.removeEventListener('focus', settle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const remeasure = useCallback(() => settleRef.current(), []);

  return { ...box, remeasure };
}
